<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';
import { ATTACHMENT_TYPES } from 'dashboard/components-next/message/constants';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import MediaViewer from 'dashboard/components-next/media/MediaViewer.vue';

const props = defineProps({
  attachments: { type: Array, default: () => [] },
  showContact: { type: Boolean, default: false },
});

const { t } = useI18n();

const viewerRef = ref(null);

const isImageType = type => type === ATTACHMENT_TYPES.IMAGE;
const isVideoType = type =>
  [ATTACHMENT_TYPES.VIDEO, ATTACHMENT_TYPES.IG_REEL].includes(type);
const isAudioType = type => type === ATTACHMENT_TYPES.AUDIO;

const fileNameFromUrl = url => {
  if (!url) return '';
  const name = url.split('/').pop();
  return name ? decodeURIComponent(name.split('?')[0]) : '';
};

const fileIcon = type => {
  if (isAudioType(type)) return 'i-lucide-music';
  if (isVideoType(type)) return 'i-lucide-video';
  if (isImageType(type)) return 'i-lucide-image';
  return 'i-lucide-file';
};

const previewSrc = attachment => {
  if (isImageType(attachment.file_type)) {
    return attachment.thumb_url || attachment.data_url;
  }
  if (isVideoType(attachment.file_type)) {
    return attachment.thumb_url || null;
  }
  return null;
};

const displayTime = attachment => {
  if (!attachment.created_at) return '';
  return shortTimestamp(dynamicTime(attachment.created_at), true);
};

const directionLabel = attachment => {
  if (!attachment.direction) return '';
  return attachment.direction === 'sent'
    ? t('MEDIA_LIBRARY.DIRECTION.SENT')
    : t('MEDIA_LIBRARY.DIRECTION.RECEIVED');
};

const items = computed(() =>
  [...props.attachments].sort(
    (a, b) => (b.created_at || 0) - (a.created_at || 0)
  )
);

const onOpen = attachment => {
  viewerRef.value?.open(attachment);
};
</script>

<template>
  <div
    class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
  >
    <button
      v-for="attachment in items"
      :key="attachment.id"
      type="button"
      class="flex flex-col overflow-hidden text-left transition-all border rounded-lg cursor-pointer border-n-weak bg-n-solid-1 hover:shadow-md hover:-translate-y-px focus:outline-none focus-visible:ring-2 focus-visible:ring-n-blue-9"
      @click="onOpen(attachment)"
    >
      <div
        class="relative flex items-center justify-center w-full overflow-hidden aspect-square bg-n-slate-3"
      >
        <img
          v-if="previewSrc(attachment)"
          :src="previewSrc(attachment)"
          class="object-cover w-full h-full"
          loading="lazy"
          :alt="fileNameFromUrl(attachment.data_url)"
        />
        <Icon
          v-else
          :icon="fileIcon(attachment.file_type)"
          class="size-8 text-n-slate-10"
        />
        <span
          v-if="attachment.direction"
          class="absolute px-1.5 py-0.5 text-xxs font-medium rounded top-1.5 ltr:left-1.5 rtl:right-1.5"
          :class="
            attachment.direction === 'sent'
              ? 'bg-n-teal-9 text-white'
              : 'bg-n-slate-9 text-white'
          "
        >
          {{ directionLabel(attachment) }}
        </span>
      </div>
      <div class="flex flex-col gap-0.5 p-2">
        <span class="text-xs font-medium truncate text-n-slate-12">
          {{ fileNameFromUrl(attachment.data_url) || attachment.extension }}
        </span>
        <span
          v-if="showContact && attachment.contact"
          class="text-xxs truncate text-n-slate-11"
        >
          {{ attachment.contact.name }}
        </span>
        <span class="text-xxs text-n-slate-10">
          {{ displayTime(attachment) }}
        </span>
      </div>
    </button>
    <MediaViewer ref="viewerRef" :show-contact="showContact" />
  </div>
</template>
